import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import { imageUrl } from "../../../api/apiConfig/apiConfig";
import "../Home/Homepage.css";
import "./ExchangeWalletPage.css";

const resolveCoinIconUrl = (path) => {
  if (!path || typeof path !== "string") return "";
  const p = path.trim();
  if (/^https?:\/\//i.test(p)) return p;
  const base = String(imageUrl || "").replace(/\/$/, "");
  return `${base}/${p.replace(/^\//, "")}`;
};

const ExchangeWalletPage = () => {
  const [wallets, setWallets] = useState([]);
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [walletsError, setWalletsError] = useState("");
  const [coins, setCoins] = useState([]);
  const [coinLoading, setCoinLoading] = useState(false);
  const [coinError, setCoinError] = useState("");
  const [coinDropdownOpen, setCoinDropdownOpen] = useState(false);
  const [walletSearch, setWalletSearch] = useState("");
  const [selectedCoin, setSelectedCoin] = useState("USDT");
  const [chainFilter, setChainFilter] = useState("main");
  const [, setChainListSubmitted] = useState(false);
  const coinDropdownRef = useRef(null);
  const [walletTypes, setWalletTypes] = useState([]);
  const [walletTypesLoading, setWalletTypesLoading] = useState(false);
  const [walletTypesError, setWalletTypesError] = useState("");
  const [txModal, setTxModal] = useState(null);
  const [txForm, setTxForm] = useState({
    type: "DEBIT",
    amount: "",
    account_type: "locked_balance",
    wallet_type: "main",
    coinId: "",
  });
  const [txSubmitting, setTxSubmitting] = useState(false);
  const [txMessage, setTxMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    let mounted = true;
    const fetchWalletTypes = async () => {
      setWalletTypesLoading(true);
      setWalletTypesError("");
      try {
        const res = await AdminService.getAdminExchangeWalletTypes();
        if (!mounted) return;
        if (res?.success && Array.isArray(res.data)) {
          const normalized = res.data
            .map((x) => String(x ?? "").trim())
            .filter(Boolean);
          setWalletTypes(normalized);
          if (normalized.includes("main")) setChainFilter("main");
        } else {
          setWalletTypes([]);
          setWalletTypesError(res?.message || "Failed to load wallet types");
        }
      } catch {
        if (!mounted) return;
        setWalletTypes([]);
        setWalletTypesError("Failed to load wallet types");
      } finally {
        if (mounted) setWalletTypesLoading(false);
      }
    };
    fetchWalletTypes();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchCoins = async () => {
      setCoinLoading(true);
      setCoinError("");
      try {
        const res = await AdminService.getAdminExchangeAllCoins();
        if (!mounted) return;
        if (res?.success && Array.isArray(res.data)) {
          const normalized = res.data
            .map((c) => ({
              id: c?._id ?? c?.id ?? c?.short_name,
              short_name: String(c?.short_name ?? "").trim(),
              icon_path: c?.icon_path ?? "",
            }))
            .filter((c) => c.id && c.short_name);
          setCoins(normalized);
          const hasUsdt = normalized.some((c) => c.short_name.toUpperCase() === "USDT");
          if (hasUsdt) setSelectedCoin("USDT");
          else if (normalized.length) setSelectedCoin(normalized[0].short_name);
        } else {
          setCoins([]);
          setCoinError(res?.message || "Failed to load coins");
        }
      } catch {
        if (!mounted) return;
        setCoins([]);
        setCoinError("Failed to load coins");
      } finally {
        if (mounted) setCoinLoading(false);
      }
    };
    fetchCoins();
    return () => {
      mounted = false;
    };
  }, []);

  const fetchWalletDetails = async (coinName, walletType) => {
    if (!coinName || !walletType) return;
    setWalletsLoading(true);
    setWalletsError("");
    try {
      const res = await AdminService.getAdminExchangeWalletDetailsByCoin(coinName, walletType);
      if (res?.success && Array.isArray(res.data)) {
        setWallets(res.data);
      } else {
        setWallets([]);
        setWalletsError(res?.message || "Failed to load wallet details");
      }
    } catch {
      setWallets([]);
      setWalletsError("Failed to load wallet details");
    } finally {
      setWalletsLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedCoin || !chainFilter) return;
    fetchWalletDetails(selectedCoin, chainFilter);
  }, [selectedCoin, chainFilter]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (coinDropdownRef.current && !coinDropdownRef.current.contains(event.target)) {
        setCoinDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const chainOptions = useMemo(() => {
    if (walletTypes.length) return walletTypes;
    return ["main", "spot", "p2p", "futures", "swap", "earning"];
  }, [walletTypes]);

  const filteredWallets = wallets.filter(
    (w) =>
      w.userId?.toLowerCase().includes(walletSearch.toLowerCase()) ||
      w.emailId?.toLowerCase().includes(walletSearch.toLowerCase()) ||
      w.short_name?.toLowerCase().includes(walletSearch.toLowerCase())
  );

  const handleChainListSubmit = (e) => {
    e.preventDefault();
    setChainListSubmitted(true);
    fetchWalletDetails(selectedCoin, chainFilter);
  };

  const openTxModal = (row) => {
    setTxMessage({ type: "", text: "" });
    setTxForm({
      type: "DEBIT",
      amount: "",
      account_type: "locked_balance",
      wallet_type: row.wallet_type || chainFilter || "main",
      coinId: row.currency_id || "",
    });
    setTxModal({
      userId: row.userId,
      coinId: row.currency_id,
      wallet_type: row.wallet_type || chainFilter || "main",
      short_name: row.short_name,
      emailId: row.emailId,
      uuid: row.uuid,
    });
  };

  const closeTxModal = () => {
    if (txSubmitting) return;
    setTxModal(null);
    setTxMessage({ type: "", text: "" });
  };

  const submitDebitCredit = async (e) => {
    e.preventDefault();
    if (!txModal) return;
    const amountNum = Number(txForm.amount);
    if (Number.isNaN(amountNum) || amountNum <= 0) {
      setTxMessage({ type: "error", text: "Amount must be greater than 0" });
      return;
    }
    const payload = {
      userId: txModal.userId,
      coinId: txForm.coinId || txModal.coinId,
      type: txForm.type,
      amount: amountNum,
      account_type: txForm.account_type,
      wallet_type: txForm.wallet_type || txModal.wallet_type,
    };
    setTxSubmitting(true);
    setTxMessage({ type: "", text: "" });
    try {
      const res = await AdminService.adminExchangeDebitCreditByAdmin(payload);
      if (res?.success) {
        setTxMessage({ type: "success", text: res?.message || "Request submitted successfully" });
        await fetchWalletDetails(selectedCoin, chainFilter);
        setTimeout(() => {
          setTxModal(null);
          setTxMessage({ type: "", text: "" });
        }, 600);
      } else {
        setTxMessage({ type: "error", text: res?.message || "Failed to submit request" });
      }
    } catch {
      setTxMessage({ type: "error", text: "Failed to submit request" });
    } finally {
      setTxSubmitting(false);
    }
  };

  const selectedCoinObj =
    coins.find((c) => c.short_name === selectedCoin) ??
    (coins.length ? coins[0] : null);

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Exchange Wallet</span>
          </p>
          <h1 className="dashboard-hero__title">Exchange Wallet</h1>
          <p className="dashboard-hero__subtitle">
            View exchange wallet balances and supported coins
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-wallet" />
        </div>
      </section>

      <div className="dashboard-body">
        {/* Wallet list */}
        <section className="exwallet-section">
          <div className="exwallet-section__header">
            <div className="exwallet-section__title-wrap">
              <span className="exwallet-section__icon">
                <i className="fas fa-coins" />
              </span>
              <h2 className="exwallet-section__title">Wallet Balances</h2>
            </div>
            <div className="exwallet-section__stats">
              <span className="exwallet-stat">
                <strong>{wallets.length}</strong> Wallets
              </span>
            </div>
          </div>

          <div className="exwallet-toolbar">
            <form className="exwallet-chain-form" onSubmit={handleChainListSubmit}>
              <div className="exwallet-coin-dropdown" ref={coinDropdownRef}>
                <button
                  type="button"
                  className="exwallet-coin-dropdown__toggle"
                  onClick={() => setCoinDropdownOpen((v) => !v)}
                  aria-label="Select currency"
                  disabled={coinLoading}
                >
                  {selectedCoinObj ? (
                    <>
                      {resolveCoinIconUrl(selectedCoinObj.icon_path) ? (
                        <img
                          src={resolveCoinIconUrl(selectedCoinObj.icon_path)}
                          alt={`${selectedCoinObj.short_name} icon`}
                          className="exwallet-coin-dropdown__icon"
                          width={20}
                          height={20}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <span className="exwallet-coin-dropdown__icon exwallet-coin-dropdown__icon--placeholder" aria-hidden>
                          <i className="fas fa-coins" />
                        </span>
                      )}
                      <span>{selectedCoinObj.short_name}</span>
                    </>
                  ) : (
                    <span>{coinLoading ? "Loading..." : "Select coin"}</span>
                  )}
                  <i className="fas fa-chevron-down" aria-hidden />
                </button>
                {coinDropdownOpen && (
                  <div className="exwallet-coin-dropdown__menu" role="listbox" aria-label="Coins">
                    {coins.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className={`exwallet-coin-dropdown__item ${selectedCoinObj?.short_name === c.short_name ? "is-active" : ""}`}
                        onClick={() => {
                          setSelectedCoin(c.short_name);
                          setCoinDropdownOpen(false);
                        }}
                      >
                        {resolveCoinIconUrl(c.icon_path) ? (
                          <img
                            src={resolveCoinIconUrl(c.icon_path)}
                            alt={`${c.short_name} icon`}
                            className="exwallet-coin-dropdown__icon"
                            width={20}
                            height={20}
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <span className="exwallet-coin-dropdown__icon exwallet-coin-dropdown__icon--placeholder" aria-hidden>
                            <i className="fas fa-coins" />
                          </span>
                        )}
                        <span>{c.short_name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <select
                className="exwallet-chain-input"
                value={chainFilter}
                onChange={(e) => setChainFilter(e.target.value)}
                aria-label="Chain or wallet type"
                disabled={walletTypesLoading}
              >
                <option value="">All wallet types</option>
                {chainOptions.map((t) => (
                  <option key={t} value={t}>
                    {String(t).toUpperCase()}
                  </option>
                ))}
              </select>
              <button type="submit" className="exwallet-chain-submit">
                <i className="fas fa-search" /> Submit
              </button>
            </form>
            <div className="exwallet-search-wrap">
              <i className="fas fa-search" />
              <input
                type="text"
                placeholder="Search by user ID, email or currency..."
                value={walletSearch}
                onChange={(e) => setWalletSearch(e.target.value)}
                className="exwallet-search-input"
              />
            </div>
          </div>
          {walletTypesError && (
            <p className="exwallet-empty">{walletTypesError}</p>
          )}
          {coinError && (
            <p className="exwallet-empty">{coinError}</p>
          )}
          {walletsError && (
            <p className="exwallet-empty">{walletsError}</p>
          )}
          <div className="exwallet-table-wrap">
            <table className="exwallet-table">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Email</th>
                  <th>Currency</th>
                  <th>Balance</th>
                  <th>Locked Balance</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {walletsLoading ? (
                  <tr>
                    <td colSpan="6" className="exwallet-empty">
                      <i className="fas fa-spinner fa-spin" /> Loading...
                    </td>
                  </tr>
                ) : (
                  filteredWallets.map((row, idx) => (
                    <tr key={row._id ?? `${row.userId ?? row.uuid ?? idx}-${row.currency_id ?? idx}`}>
                      <td><span className="exwallet-mono">{row.uuid ?? "—"}</span></td>
                      <td>{row.emailId ?? "—"}</td>
                      <td><strong>{row.short_name ?? "—"}</strong></td>
                      <td>{typeof row.balance === "number" ? row.balance.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 8 }) : row.balance}</td>
                      <td>{typeof row.locked_balance === "number" ? row.locked_balance.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 8 }) : row.locked_balance}</td>
                      <td>
                        <button
                          type="button"
                          className="exwallet-action-btn"
                          onClick={() => openTxModal(row)}
                        >
                          Debit/Credit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!walletsLoading && filteredWallets.length === 0 && (
            <p className="exwallet-empty">No wallets match your search.</p>
          )}
        </section>

        {/* Coin list - with dropdown + main input + Submit */}

      </div>

      {txModal && (
        <div className="exwallet-modal-overlay" onClick={closeTxModal}>
          <div className="exwallet-modal" onClick={(e) => e.stopPropagation()}>
            <div className="exwallet-modal__head">
              <h3>Debit / Credit Wallet</h3>
              <button type="button" onClick={closeTxModal} className="exwallet-modal__close" aria-label="Close">×</button>
            </div>
            <form className="exwallet-modal__body" onSubmit={submitDebitCredit}>
              <p className="exwallet-modal__meta">
                User: <strong>{txModal.uuid || txModal.userId}</strong> ({txModal.emailId || "—"})
              </p>
              <div className="exwallet-modal__grid">
                <label>
                  Select Coin
                  <select
                    value={txForm.coinId}
                    onChange={(e) => setTxForm((f) => ({ ...f, coinId: e.target.value }))}
                    required
                  >
                    {coins.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.short_name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Wallet Type
                  <select
                    value={txForm.wallet_type}
                    onChange={(e) => setTxForm((f) => ({ ...f, wallet_type: e.target.value }))}
                    required
                  >
                    {chainOptions.map((t) => (
                      <option key={t} value={t}>
                        {String(t).toUpperCase()}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Type
                  <select value={txForm.type} onChange={(e) => setTxForm((f) => ({ ...f, type: e.target.value }))}>
                    <option value="DEBIT">DEBIT</option>
                    <option value="CREDIT">CREDIT</option>
                  </select>
                </label>
                <label>
                  Account Type
                  <select value={txForm.account_type} onChange={(e) => setTxForm((f) => ({ ...f, account_type: e.target.value }))}>
                    <option value="balance">Balance</option>
                    <option value="locked_balance">Locked Balance</option>
                  </select>
                </label>
                <label className="exwallet-modal__span2">
                  Amount
                  <input
                    type="number"
                    min="0.00000001"
                    step="any"
                    value={txForm.amount}
                    onChange={(e) => setTxForm((f) => ({ ...f, amount: e.target.value }))}
                    placeholder="Enter amount"
                    required
                  />
                </label>
              </div>
              {txMessage.text && (
                <p className={`exwallet-modal__msg exwallet-modal__msg--${txMessage.type}`}>{txMessage.text}</p>
              )}
              <div className="exwallet-modal__actions">
                <button type="button" onClick={closeTxModal} disabled={txSubmitting}>Cancel</button>
                <button type="submit" disabled={txSubmitting}>
                  {txSubmitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default ExchangeWalletPage;
